import Button from '@atlaskit/button/new';
import Textfield from '@atlaskit/textfield';
import { Field } from '@atlaskit/form';
import Modal, {
  ModalTransition,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '@atlaskit/modal-dialog';
import t from 'modules/i18n/intl';
import React, { useEffect, useState } from 'react';
import { messages } from 'modules/mac/messages';
import { FormattedMessage } from 'react-intl';

import {
  fetchAddNewPartnerContact,
  fetchGetCustomerByEmail,
} from 'modules/mac/duck/actions/contact';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAddNewPartnerContact,
  selectAddNewPartnerContactSuccess,
  selectGetCustomerByEmail,
} from 'modules/mac/duck/selectors';
import { StyledModalInputWrapper } from './licensesExpandedRow/StyledComponents';
import validateEmailAddress from 'util/email';

export const AddPartnerContactModal = ({ isOpen, closeModal, switchedUserDetails }) => {
  const dispatch = useDispatch();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [customerData, setCustomerData] = useState<any>();
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [isNewContact, setIsNewContact] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);

  const customerByEmail = useSelector(selectGetCustomerByEmail);
  const addNewPartnerContact = useSelector(selectAddNewPartnerContact);
  const addNewPartnerContactSuccess = useSelector(selectAddNewPartnerContactSuccess);

  useEffect(() => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setIsNewContact(false);
    setIsSaveDisabled(false);
  }, [isOpen]);

  useEffect(() => {
    if (customerByEmail[email]) {
      setCustomerData(customerByEmail[email]);
    }
  }, [customerByEmail[email]]);

  useEffect(() => {
    if (customerData?._result?.contactDetails) {
      const contactDetailsData = {
        contactDetails: {
          email: customerData._result?.contactDetails?.email,
          firstName: customerData._result?.contactDetails?.firstName,
          lastName: customerData._result?.contactDetails?.lastName,
          phone: customerData._result?.contactDetails?.phone,
        },
      };

      dispatch(fetchAddNewPartnerContact(contactDetailsData));
    }
  }, [customerData]);

  useEffect(() => {
    if (customerData?._result === '' && (!firstName || !lastName)) {
      setIsNewContact(true);
      setIsSaveDisabled(true);
    } else if (customerData?._result === '' && firstName && lastName) {
      const contactDetailsData = {
        contactDetails: {
          email,
          firstName,
          lastName,
          phone,
        },
        organisationDetails: {
          ...switchedUserDetails?.organisationDetails,
        },
      };
      dispatch(fetchAddNewPartnerContact(contactDetailsData));
    }
  }, [customerData]);

  useEffect(() => {
    if (addNewPartnerContactSuccess) {
      closeModal();
    }
  }, [addNewPartnerContactSuccess]);

  const onClickSaveContact = () => {
    if (validateEmailAddress(email)) {
      dispatch(fetchGetCustomerByEmail(email));
    } else {
      setInvalidEmail(true);
    }
    setIsNewContact(false);
  };

  return (
    <ModalTransition>
      {isOpen && (
        <Modal autoFocus onClose={closeModal}>
          <ModalHeader>
            <ModalTitle>
              <FormattedMessage {...messages.addNewContact} />
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            <StyledModalInputWrapper>
              <Field label={t('mac.licenses.new.contact.email')} name="email" isRequired>
                {({ fieldProps }: any) => (
                  <Textfield
                    {...fieldProps}
                    type="text"
                    value={email}
                    onChange={(e) => {
                      setInvalidEmail(false);
                      setIsSaveDisabled(false);
                      setEmail(e.target.value);
                    }}
                    data-cy="email-input"
                  />
                )}
              </Field>
            </StyledModalInputWrapper>
            <StyledModalInputWrapper>
              <Field
                label={t('checkout.contact.firstname')}
                name="first-name"
                isRequired={isNewContact}
              >
                {({ fieldProps }: any) => (
                  <Textfield
                    {...fieldProps}
                    type="text"
                    value={firstName}
                    maxLength={50}
                    onChange={(e) => {
                      setIsSaveDisabled(false);
                      setFirstName(e.target.value);
                    }}
                    data-cy="contact-firstname-input"
                  />
                )}
              </Field>
            </StyledModalInputWrapper>
            <StyledModalInputWrapper>
              <Field
                label={t('checkout.contact.lastname')}
                name="contact-lastname"
                isRequired={isNewContact}
              >
                {({ fieldProps }: any) => (
                  <Textfield
                    {...fieldProps}
                    value={lastName}
                    type="text"
                    maxLength={50}
                    onChange={(e) => {
                      setIsSaveDisabled(false);
                      setLastName(e.target.value);
                    }}
                    data-cy="contact-lastname-input"
                  />
                )}
              </Field>
            </StyledModalInputWrapper>
            <StyledModalInputWrapper>
              <Field label={t('mac.partners.contact.phone')} name="phone">
                {({ fieldProps }: any) => (
                  <Textfield
                    {...fieldProps}
                    type="phone"
                    data-testid="contact-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                )}
              </Field>
            </StyledModalInputWrapper>
          </ModalBody>
          <ModalFooter>
            <Button appearance="subtle" onClick={closeModal}>
              <FormattedMessage {...messages.addNewContactModalCancel} />
            </Button>
            <Button
              appearance="primary"
              isDisabled={invalidEmail || isSaveDisabled}
              onClick={onClickSaveContact}
              data-cy="add-partner-contact-save-btn"
              isLoading={customerByEmail?.[email]?.inProgress || addNewPartnerContact?.inProgress}
            >
              <FormattedMessage {...messages.addNewContactModalSave} />
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </ModalTransition>
  );
};
